/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, LOCALE_ID, Pipe, PipeTransform, Type} from '@angular/core';
import {NumberFormatStyle, NumberFormatter} from './intl';
import {invalidPipeArgumentError} from './invalid_pipe_argument_error';

const /** @type {?} */ _NUMBER_FORMAT_REGEXP = /^(\d+)?\.((\d+)(-(\d+))?)?$/;
/**
 * @param {?} pipe
 * @param {?} locale
 * @param {?} value
 * @param {?} style
 * @param {?=} digits
 * @param {?=} currency
 * @param {?=} currencyAsSymbol
 * @return {?}
 */
function formatNumber(
    pipe: Type<any>, locale: string, value: number | string, style: NumberFormatStyle,
    digits?: string | null, currency: string | null = null,
    currencyAsSymbol: boolean = false): string|null {
  if (value == null) return null;

  // Convert strings to numbers
  value = typeof value === 'string' && isNumeric(value) ? +value : value;
  if (typeof value !== 'number') {
    throw invalidPipeArgumentError(pipe, value);
  }

  let /** @type {?} */ minInt: number|undefined = undefined;
  let /** @type {?} */ minFraction: number|undefined = undefined;
  let /** @type {?} */ maxFraction: number|undefined = undefined;
  if (style !== NumberFormatStyle.Currency) {
    // rely on Intl default for currency
    minInt = 1;
    minFraction = 0;
    maxFraction = 3;
  }

  if (digits) {
    const /** @type {?} */ parts = digits.match(_NUMBER_FORMAT_REGEXP);
    if (parts === null) {
      throw new Error(`${digits} is not a valid digit info for number pipes`);
    }
    if (parts[1] != null) {  // min integer digits
      minInt = parseIntAutoRadix(parts[1]);
    }
    if (parts[3] != null) {  // min fraction digits
      minFraction = parseIntAutoRadix(parts[3]);
    }
    if (parts[5] != null) {  // max fraction digits
      maxFraction = parseIntAutoRadix(parts[5]);
    }
  }

  return NumberFormatter.format( /** @type {?} */((value as number)), locale, style, {
    minimumIntegerDigits: minInt,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
    currency: currency,
    currencyAsSymbol: currencyAsSymbol,
  });
}
/**
 * \@ngModule CommonModule
 * \@whatItDoes Formats a number according to locale rules.
 * \@howToUse `number_expression | number[:digitInfo]`
 * 
 * Formats a number as text. Group sizing and separator and other locale-specific
 * configurations are based on the active locale.
 * 
 * where `expression` is a number:
 *  - `digitInfo` is a `string` which has a following format: <br>
 *     <code>{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}</code>
 *   - `minIntegerDigits` is the minimum number of integer digits to use. Defaults to `1`.
 *   - `minFractionDigits` is the minimum number of digits after fraction. Defaults to `0`.
 *   - `maxFractionDigits` is the maximum number of digits after fraction. Defaults to `3`.
 * 
 * For more information on the acceptable range for each of these numbers and other
 * details see your native internationalization library.
 * 
 * WARNING: this pipe uses the Internationalization API which is not yet available in all browsers
 * and may require a polyfill. See [Browser Support](guide/browser-support) for details.
 * 
 * ### Example
 * 
 * {\@example common/pipes/ts/number_pipe.ts region='NumberPipe'}
 * 
 * \@stable
 */
export class DecimalPipe implements PipeTransform {
/**
 * @param {?} _locale
 */
constructor(
private _locale: string) {}
/**
 * @param {?} value
 * @param {?=} digits
 * @return {?}
 */
transform(value: any, digits?: string): string|null {
    return formatNumber(DecimalPipe, this._locale, value, NumberFormatStyle.Decimal, digits);
  }
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'number'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [LOCALE_ID, ] }, ]},
];
}

function DecimalPipe_tsickle_Closure_declarations() {
/** @type {?} */
DecimalPipe.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DecimalPipe.ctorParameters;
/** @type {?} */
DecimalPipe.prototype._locale;
}

/**
 * \@ngModule CommonModule
 * \@whatItDoes Formats a number as a percentage according to locale rules.
 * \@howToUse `number_expression | percent[:digitInfo]`
 * 
 * \@description 
 * 
 * Formats a number as percentage.
 * 
 * - `digitInfo` See {\@link DecimalPipe} for detailed description.
 * 
 * WARNING: this pipe uses the Internationalization API which is not yet available in all browsers
 * and may require a polyfill. See [Browser Support](guide/browser-support) for details.
 * 
 * ### Example
 * 
 * {\@example common/pipes/ts/number_pipe.ts region='PercentPipe'}
 * 
 * \@stable
 */
export class PercentPipe implements PipeTransform {
/**
 * @param {?} _locale
 */
constructor(
private _locale: string) {}
/**
 * @param {?} value
 * @param {?=} digits
 * @return {?}
 */
transform(value: any, digits?: string): string|null {
    return formatNumber(PercentPipe, this._locale, value, NumberFormatStyle.Percent, digits);
  }
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'percent'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [LOCALE_ID, ] }, ]},
];
}

function PercentPipe_tsickle_Closure_declarations() {
/** @type {?} */
PercentPipe.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PercentPipe.ctorParameters;
/** @type {?} */
PercentPipe.prototype._locale;
}

/**
 * \@ngModule CommonModule
 * \@whatItDoes Formats a number as currency using locale rules.
 * \@howToUse `number_expression | currency[:currencyCode[:symbolDisplay[:digitInfo]]]`
 * \@description 
 * 
 * Use `currency` to format a number as currency.
 * 
 * - `currencyCode` is the [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code, such
 *    as `USD` for the US dollar and `EUR` for the euro.
 * - `symbolDisplay` is a boolean indicating whether to use the currency symbol or code.
 *   - `true`: use symbol (e.g. `$`).
 *   - `false`(default): use code (e.g. `USD`).
 * - `digitInfo` See {\@link DecimalPipe} for detailed description.
 * 
 * WARNING: this pipe uses the Internationalization API which is not yet available in all browsers
 * and may require a polyfill. See [Browser Support](guide/browser-support) for details.
 * 
 * ### Example
 * 
 * {\@example common/pipes/ts/number_pipe.ts region='CurrencyPipe'}
 * 
 * \@stable
 */
export class CurrencyPipe implements PipeTransform {
/**
 * @param {?} _locale
 */
constructor(
private _locale: string) {}
/**
 * @param {?} value
 * @param {?=} currencyCode
 * @param {?=} symbolDisplay
 * @param {?=} digits
 * @return {?}
 */
transform(
      value: any, currencyCode: string = 'USD', symbolDisplay: boolean = false,
      digits?: string): string|null {
    return formatNumber(
        CurrencyPipe, this._locale, value, NumberFormatStyle.Currency, digits, currencyCode,
        symbolDisplay);
  }
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'currency'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Inject, args: [LOCALE_ID, ] }, ]},
];
}

function CurrencyPipe_tsickle_Closure_declarations() {
/** @type {?} */
CurrencyPipe.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CurrencyPipe.ctorParameters;
/** @type {?} */
CurrencyPipe.prototype._locale;
}

/**
 * @param {?} text
 * @return {?}
 */
function parseIntAutoRadix(text: string): number {
  const /** @type {?} */ result: number = parseInt(text);
  if (isNaN(result)) {
    throw new Error('Invalid integer literal when parsing ' + text);
  }
  return result;
}
/**
 * @param {?} value
 * @return {?}
 */
export function isNumeric(value: any): boolean {
  return !isNaN(value - parseFloat(value));
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
